import { RegistryItem } from "../lib/supabase";

export interface CreateRegistryItemData {
  name: string;
  description: string;
  price: number;
  image_url?: string;
  registry_url: string;
  category: string;
  type?: "item" | "external_link";
}

export interface UpdateRegistryItemData {
  name?: string;
  description?: string;
  price?: number;
  image_url?: string;
  registry_url?: string;
  category?: string;
  type?: "item" | "external_link";
  purchased?: boolean;
  purchased_by_id?: string;
  purchased_by_name?: string;
  purchase_note?: string;
}

export interface ScrapedProductData {
  name: string | null;
  price: number | null;
  description: string | null;
  image_url: string | null;
  category: string | null;
  registry_url: string;
}

export interface ScrapeProductResponse {
  success: boolean;
  error?: string;
  data: ScrapedProductData;
}

class RegistryAPI {
  private baseUrl = "/api/registry";

  async getAllItems(): Promise<RegistryItem[]> {
    const response = await fetch(this.baseUrl);
    if (!response.ok) {
      throw new Error("Failed to fetch registry items");
    }
    return response.json();
  }

  async createItem(itemData: CreateRegistryItemData): Promise<RegistryItem> {
    const response = await fetch(this.baseUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(itemData),
    });

    if (!response.ok) {
      throw new Error("Failed to add item");
    }

    return response.json();
  }

  async updateItem(
    itemId: string,
    updateData: UpdateRegistryItemData
  ): Promise<RegistryItem> {
    const response = await fetch(`${this.baseUrl}/${itemId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error("Failed to update item");
    }

    return response.json();
  }

  async deleteItem(itemId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${itemId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete item");
    }
  }

  async markAsPurchased(
    itemId: string,
    purchasedByName: string,
    purchasedById?: string,
    note?: string
  ): Promise<RegistryItem> {
    return this.updateItem(itemId, {
      purchased: true,
      purchased_by_name: purchasedByName,
      purchased_by_id: purchasedById,
      purchase_note: note,
    });
  }

  async scrapeProduct(url: string): Promise<ScrapeProductResponse> {
    const response = await fetch("/.netlify/functions/scrape-product", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      throw new Error("Failed to scrape product information");
    }

    return response.json();
  }
}

export const registryAPI = new RegistryAPI();
